/*
 * Draft 12 in the click-through preview: the Marketing suite (Google profile, ranks, ads, social, cold email) with
 * made-up data kept in memory, one set per company, checked by the same rules the server uses. Nothing posts or sends.
 */
import {
  addDays,
  channelReturn,
  gbpDescriptionProblems,
  gbpPostProblems,
  GRID_MISS,
  circleSpots,
  gridMilesApart,
  gridSummary,
  milesBetween,
  mapRankKeywords,
  outreachFromProblem,
  placeMapsUrl,
  placeChange,
  replyDraft,
  replyProblems,
  socialPostProblems,
  warmupCap,
  webRankWindows,
  type AdsAnswer,
  type Channel,
  type FinderRow,
  type GbpAnswer,
  type GbpPostRow,
  type MediaRow,
  type OutreachAnswer,
  type PageBriefRow,
  type ProspectRow,
  type RankGridRow,
  type RankCircle,
  type RankRun,
  type ReviewRow,
  type SocialPostRow,
  type VisitPhotoPick,
  type WebRankRow,
} from "@steward/shared";

type Answer = Promise<{ status: number; body: unknown }>;
type Json = Record<string, unknown>;
const ok = (body: unknown): Answer => Promise.resolve({ status: 200, body });
const fail = (status: number, code: string, message: string): Answer => Promise.resolve({ status, body: { code, message } });
const NO = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
const daysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();
let n = 0;
const id = () => `00000000-0000-4000-8000-${String(900000000000 + (n += 1))}`;
const str = (v: unknown) => (typeof v === "string" ? v : "");

interface State {
  gbp: GbpAnswer;
  media: MediaRow[];
  photos: VisitPhotoPick[];
  social: SocialPostRow[];
  grids: RankGridRow[];
  web: { site: string; tracked: WebRankRow[]; top: WebRankRow[]; checkedAt: string };
  briefs: PageBriefRow[];
  ads: AdsAnswer;
  outreach: OutreachAnswer;
  finder: FinderRow[];
}

const states = new Map<string, State>();

/** Two listings, as the Cleaners of New Orleans has (New Orleans and Metairie), each with its own circle. */
const DEMO_LISTINGS = [
  { id: "00000000-0000-4000-8000-00000000a001", name: "The Cleaners of New Orleans", lat: 29.9301, lng: -90.0446 },
  { id: "00000000-0000-4000-8000-00000000a002", name: "The Cleaners of Metairie", lat: 30.0, lng: -90.165 },
];
const DEMO_AREAS: RankCircle[] = DEMO_LISTINGS.map((l) => ({
  listing: l.id,
  lat: l.lat,
  lng: l.lng,
  size: 9,
  radius: 10,
  skip: [],
}));

/** A made-up run: each listing best near its own middle, worse further out, a little worse each week back. */
function demoRun(areas: RankCircle[], best: number, weeksBack: number, competitor: string): RankRun {
  const spots = circleSpots(areas);
  const who = areas.map((c) => ({ id: c.listing, name: DEMO_LISTINGS.find((l) => l.id === c.listing)?.name ?? "Your business" }));
  const each = spots.map((p, k) =>
    areas.map((c, j) => {
      const at = best + j * 2 + weeksBack + Math.round(milesBetween(p, c) / 2.2) + ((k * 7 + j * 3) % 3);
      return at > 20 ? null : at;
    }),
  );
  const pts = each.map((e) => {
    const f = e.filter((x): x is number => x !== null);
    return f.length ? Math.min(...f) : null;
  });
  const others = ["Bayou Maids", "Crescent Clean Co", "Pelican Pros", "Magnolia Home"];
  return {
    runOn: addDays(today(), -2 - 7 * weeksBack),
    ...gridSummary(pts),
    spots: spots.map((x): [number, number] => [x.lat, x.lng]),
    points: pts,
    tops: each.map((e, k) =>
      [0, 1, 2].map((j) => {
        const mine = e.findIndex((r) => r === j + 1);
        return mine >= 0 ? who[mine]!.name : j === 0 ? competitor : others[(k + j) % others.length]!;
      }),
    ),
    who,
    each,
    circles: areas.map((c) => ({ lat: c.lat, lng: c.lng, radius: c.radius })),
  };
}

function grid(keyword: string, best: number, competitor: string, areas: RankCircle[]): RankGridRow {
  const runs = [0, 1, 2, 3].map((w) => demoRun(areas, best, w, competitor));
  const area = areas[0]!;
  const l = runs[0]!;
  const s = { average: l.average, top3: l.top3, found: l.found };
  return {
    id: id(),
    keyword,
    lat: area.lat,
    lng: area.lng,
    size: area.size,
    radius: area.radius,
    skip: area.skip,
    areas,
    miles: gridMilesApart(area.radius, area.size),
    competitors: [competitor],
    lastRunAt: daysAgo(2),
    latest: {
      runOn: l.runOn,
      points: l.points,
      ...s,
      competitors: [{ name: competitor, average: Math.min(GRID_MISS, s.average + 3.4) }],
      rivals: [
        { name: competitor, ahead: 14, best: 1, average: Math.max(1, s.average - 2.1), rating: 4.9, reviews: 312 },
        { name: "Bayou Maids", ahead: 9, best: 2, average: Math.max(1, s.average - 0.8), rating: 4.7, reviews: 148 },
        { name: "Crescent Clean Co", ahead: 4, best: 3, average: s.average + 1.5, rating: 4.4, reviews: 61 },
      ],
      us: { rating: 4.9, reviews: 87 },
    },
    trend: [...runs].reverse().map((r) => ({ runOn: r.runOn, average: r.average })),
    runs,
  };
}

function webRow(keyword: string, kind: "tracked" | "top", pos: number, prior: number, clicks: number, seen: number): WebRankRow {
  return {
    keyword,
    kind,
    position: pos,
    prior,
    change: placeChange(pos, prior),
    clicks,
    impressions: seen,
    ctr: Math.round((clicks / seen) * 1000) / 10,
    page: `https://www.thecleanersusa.com/${keyword.split(" ").slice(0, 2).join("-")}/`,
  };
}

function seed(companyName: string): State {
  const t = today();
  const reviews: ReviewRow[] = [
    ["Ann Boudreaux", 5, "On time, kind and the house looked great. They even did the baseboards."],
    ["Marcus Landry", 5, "Best cleaning we have had. Same two ladies every time."],
    ["Lena Nguyen", 3, "They missed the hall baseboards but came back the next day."],
    ["Paul S.", 5, "Easy to book and the kitchen shines."],
    ["Rosa Hebert", 4, "Good work. A little late the first visit."],
    ["Theo Reed", 5, ""],
  ].map(([author, stars, text], i) => ({
    id: id(),
    author: author as string,
    stars: stars as number,
    text: text as string,
    postedAt: daysAgo(2 + i * 5),
    clientId: i === 1 ? id() : null,
    reply: i >= 3 ? "Thank you. We are glad the visit went well and we look forward to the next one." : null,
    replyStatus: i >= 3 ? "posted" : "none",
    suggested: replyDraft({
      author: author as string,
      stars: stars as number,
      brandPhone: "+15045550100",
      brand: "The Cleaners",
      city: "New Orleans",
      trade: "cleaning",
      text: text as string,
    }),
  }));
  const media: MediaRow[] = ["kitchen", "living room", "bath sink", "toilet", "floors", "windows"].map((tag, i) => ({
    id: id(),
    fileId: id(),
    url: "",
    caption: `After, ${tag}`,
    tags: [tag],
    source: "visit",
    blocked: tag === "toilet",
    used: i === 1 || i === 4 ? 1 : 0,
    createdAt: daysAgo(i * 3),
  }));
  const channels: [Channel, number, number, number, number][] = [
    ["lsa", 22, 9, 612000, 145000],
    ["gads", 18, 6, 388000, 192000],
    ["gbp", 15, 8, 450000, 0],
    ["facebook", 11, 3, 126000, 88000],
    ["referral", 7, 5, 301000, 0],
    ["email", 3, 1, 42000, 0],
  ];
  const days = Array.from({ length: 30 }, (_v, i) => {
    const d = addDays(t, -30 + i);
    return {
      day: d,
      gads: 5200 + ((i * 731) % 2400),
      lsa: i % 3 === 0 ? 0 : 4300 + ((i * 377) % 1800),
      facebook: 2600 + ((i * 211) % 1500),
    };
  });
  const sum = (k: "gads" | "lsa" | "facebook") => days.reduce((a, d) => a + d[k], 0);
  const prospects: ProspectRow[] = [
    [
      "Bright Dental",
      "Marcus",
      "marcus@brightdental.com",
      "Your dental office has been on Magazine Street since 1987 for local families.",
      "active",
      1,
    ],
    ["Crescent Law Group", null, "info@crescentlaw.com", null, "active", 0],
    [
      "Magnolia Pediatrics",
      "Ivy",
      "ivy@magnoliapeds.com",
      "Your clinic has cared for kids on Prytania Street since 1994.",
      "replied",
      2,
    ],
    ["Bayou Realty", "Sam", "sam@bayourealty.com", null, "new", 0],
    ["Oak Street Studio", null, "hello@oakstreetstudio.com", null, "new", 0],
    ["River Partners", "Dana", "dana@riverpartners.com", null, "unsub", 1],
    [
      "Pelican Vet Clinic",
      null,
      "office@pelicanvet.com",
      "Your vet clinic on Maple Street has served pets since 2004.",
      "active",
      1,
    ],
    ["Levee Works", null, "info@leveeworks.com", null, "paused", 0],
  ].map(([business, firstName, email, aiLine, status, step]) => ({
    id: id(),
    business: business as string,
    firstName: firstName as string | null,
    email: email as string,
    website: `https://www.${(email as string).split("@")[1]!}`,
    category: "Office",
    zip: "70115",
    sequenceId: null,
    step: step as number,
    nextAt: status === "active" ? daysAgo(-1) : null,
    status: status as ProspectRow["status"],
    aiLine: aiLine as string | null,
    note: status === "paused" ? "Missing first_name" : status === "unsub" ? "Asked to stop" : null,
    leadId: status === "replied" ? id() : null,
  }));
  const seqId = id();
  for (const p of prospects) if (p.status !== "new") p.sequenceId = seqId;
  return {
    gbp: {
      profile: {
        id: id(),
        placeId: "demo_place_1",
        name: companyName,
        address: "1234 Magazine St, New Orleans, LA 70130",
        mapsUrl: placeMapsUrl("demo_place_1"),
        description:
          "Homes in New Orleans come out spotless, and if we miss a spot we come back and redo it. Rated 4.9 stars by local families who book us again and again. Same trusted crew each visit, insured and background checked.",
        services: ["House cleaning", "Deep cleaning", "Move out cleaning"],
        rating: 4.9,
        reviewCount: 212,
        syncedAt: daysAgo(0.3),
        problems: [],
      },
      reviews,
      posts: [
        {
          id: id(),
          service: "Deep cleaning",
          area: "Uptown",
          body: "Deep cleaning in Uptown before the holidays. Every baseboard, fan and oven door done.",
          mediaId: media[0]!.id,
          status: "scheduled",
          scheduledFor: daysAgo(-2),
          postedAt: null,
          createdAt: daysAgo(1),
        },
        {
          id: id(),
          service: "Move out cleaning",
          area: "Metairie",
          body: "Move out cleaning in Metairie. Cabinets, fridge and floors done so you get the deposit back.",
          mediaId: media[1]?.id ?? media[0]!.id,
          status: "posted",
          scheduledFor: null,
          postedAt: daysAgo(4),
          createdAt: daysAgo(4),
        },
      ],
      waitingReplies: 3,
      postingConnected: false,
      managedBy: null,
    },
    media,
    photos: media.map((m) => ({
      fileId: m.fileId,
      url: "",
      client: "Ann Boudreaux",
      date: addDays(t, -3),
      kind: "after",
      inLibrary: true,
    })),
    social: [
      {
        id: id(),
        body: "Why do baseboards turn gray?\nA damp cloth once a week keeps them white.",
        mediaIds: [media[4]!.id],
        platforms: ["facebook", "instagram", "google"],
        status: "scheduled",
        scheduledFor: daysAgo(-2),
        postedAt: null,
        results: {},
        createdAt: daysAgo(1),
      },
      {
        id: id(),
        body: "What is the one room guests always see?\nWipe the entry door and the light switch first.",
        mediaIds: [media[1]!.id],
        platforms: ["facebook"],
        status: "held",
        scheduledFor: daysAgo(1),
        postedAt: null,
        results: { facebook: { ok: false, at: daysAgo(1), err: "Posting is off or not linked yet." } },
        createdAt: daysAgo(6),
      },
    ],
    grids: [
      grid("house cleaning", 1, "Maid Right", DEMO_AREAS),
      grid("maid service", 3, "Molly Maid", DEMO_AREAS),
      grid("deep cleaning", 2, "Maid Right", DEMO_AREAS),
    ],
    web: {
      site: "thecleanersusa.com",
      checkedAt: daysAgo(2),
      tracked: [
        webRow("house cleaning new orleans", "tracked", 3.2, 4.8, 61, 1840),
        webRow("maid service new orleans", "tracked", 5.6, 5.1, 22, 960),
        webRow("deep cleaning new orleans", "tracked", 7.4, 11.2, 14, 610),
        webRow("move out cleaning new orleans", "tracked", 9.8, 9.8, 6, 330),
      ],
      top: [
        webRow("cleaning service near me", "top", 12.4, 14.9, 18, 2400),
        webRow("house cleaners metairie", "top", 8.1, 7.6, 11, 720),
        webRow("the cleaners new orleans", "top", 1.1, 1.2, 140, 520),
      ],
    },
    briefs: [
      {
        id: id(),
        url: "https://www.thecleanersusa.com/deep-cleaning/",
        keyword: "deep cleaning new orleans",
        brief: {
          words: 1150,
          terms: ["deep cleaning", "baseboards", "kitchen", "move out", "green products", "ceiling fans", "inside the oven"].map(
            (term, i) => ({ term, pages: 6 - Math.floor(i / 2), lo: 1, hi: 4 }),
          ),
          topics: ["deep cleaning", "move out", "green products"],
          headings: ["What a deep clean includes", "How long it takes"],
          questions: ["How often should you deep clean?", "Do I need to be home?"],
          pages: ["https://example.com/a", "https://example.com/b", "https://example.com/c"],
        },
        score: 72,
        parts: [
          { name: "Words the top pages use", got: 78, tip: "Add ceiling fans, inside the oven" },
          { name: "Topics", got: 67, tip: "Cover the topics the top pages cover" },
          { name: "Length", got: 74, tip: "The top pages have about 1150 words. This page has 850." },
          { name: "Headings", got: 50, tip: "Add headings like the top pages have" },
          { name: "Questions answered", got: 50, tip: "Answer the questions people ask" },
        ],
        checkedAt: daysAgo(5),
      },
    ],
    ads: {
      from: addDays(t, -29),
      to: t,
      spend: [
        { platform: "gads", spendCents: sum("gads"), clicks: 412, leads: 18 },
        { platform: "lsa", spendCents: sum("lsa"), clicks: 0, leads: 22 },
        { platform: "facebook", spendCents: sum("facebook"), clicks: 655, leads: 11 },
      ],
      days,
      channels: channels.map(([channel, leads, newClients, revenueCents, spendCents]) => ({
        channel,
        leads,
        newClients,
        revenueCents,
        spendCents,
        ...channelReturn(revenueCents, spendCents),
        costPerClientCents: spendCents && newClients ? Math.round(spendCents / newClients) : null,
      })),
      conversions: [
        { id: id(), platform: "meta", event: "booked", valueCents: 24000, status: "held", eventAt: daysAgo(1) },
        { id: id(), platform: "google", event: "booked", valueCents: 24000, status: "held", eventAt: daysAgo(1) },
        { id: id(), platform: "meta", event: "paid", valueCents: 18500, status: "held", eventAt: daysAgo(3) },
      ],
      conversionsLive: false,
      spendConnected: true,
    },
    outreach: {
      sequences: [
        {
          id: seqId,
          name: "Offices uptown",
          fromName: "Olivia",
          fromEmail: "olivia@getthecleaners.com",
          mailingAddress: "100 Main St, New Orleans, LA 70115",
          steps: [
            {
              days: 0,
              subject: "Cleaning for {{business}}",
              body: "Hi {{first_name|there}},\n\n{{opener}} Would a weekly cleaning help?",
            },
            { days: 3, subject: "", body: "Following up on my note." },
            { days: 5, subject: "", body: "Last note from me. Happy to stop by for a quick look." },
          ],
          dailyCap: 30,
          ramp: true,
          sending: false,
          todayCap: warmupCap(30, true, 2),
          sentToday: 0,
          prospects: { active: 3, replied: 1, done: 0, stopped: 2 },
        },
      ],
      prospects,
      sent: prospects
        .filter((p) => p.step > 0)
        .map((p) => ({
          id: id(),
          business: p.business,
          step: 0,
          subject: `Cleaning for ${p.business}`,
          status: "dry_run",
          createdAt: daysAgo(3),
        })),
      aiLinesAllowed: true,
      finderAllowed: true,
    },
    finder: [
      {
        id: id(),
        category: "Dentist",
        area: "New Orleans",
        big: false,
        target: 60,
        pages: 3,
        pagesPaid: 2,
        found: 40,
        added: 17,
        skipped: 23,
        spare: 0,
        status: "done",
        note: "The next page had fewer than 10 new businesses, so it was not paid for.",
        createdAt: daysAgo(6),
      },
    ],
  };
}

const at = (companyId: string, name: string) => {
  let s = states.get(companyId);
  if (!s) {
    s = seed(name);
    states.set(companyId, s);
  }
  return s;
};

/** The Marketing suite's routes in the preview. gate answers whether the viewer's role (and the names switch) allows it. */
export function suiteRoute(
  gate: (a: "marketing.read" | "marketing.update" | "outreach.send") => boolean,
  company: { id: string; name: string },
  method: string,
  p: string,
  b: unknown,
): Answer | null {
  if (!p.startsWith("marketing/")) return null;
  if (!gate("marketing.read")) return NO();
  const write = method !== "GET";
  if (write && !gate(p.endsWith("/sending") ? "outreach.send" : "marketing.update")) return NO();
  const s = at(company.id, company.name);
  const body = (b ?? {}) as Json;
  const r = p.slice("marketing/".length);
  const prof = s.gbp.profile!;

  // Sign in with Google or Facebook (2026-11-01): the preview is signed in with Google, so the picker shows.
  if (r === "signin" && method === "GET")
    return ok({
      ready: { google: true, meta: true },
      google: { who: "office@example.com", at: new Date().toISOString(), until: null, own: true },
      meta: null,
    });
  if (r === "signin/start") return fail(409, "preview", "Signing in is off in the preview.");
  if (r === "signin/choices")
    return ok({
      google: {
        ads: [{ id: "123-456-7890", name: `${company.name} Ads`, managerId: "" }],
        profiles: [
          {
            placeId: "demo_place_1",
            name: company.name,
            address: "1234 Magazine St, New Orleans, LA 70130",
            location: "accounts/1/locations/1",
            onHere: s.gbp.profile ? "demo-row-1" : null,
          },
        ],
        error: null,
      },
      meta: null,
    });
  if (r.startsWith("signin/") && method === "DELETE") return ok({ removed: true });
  if (r === "gbp" && method === "GET") return ok(s.gbp);
  if (r === "gbp/sync") return ok({ ...s.gbp, profile: { ...prof, syncedAt: new Date().toISOString() } });
  if (r.startsWith("gbp/choices"))
    return ok(
      [
        [company.name, "1234 Magazine St, New Orleans, LA 70130", 212],
        [`${company.name} Metairie`, "800 Veterans Blvd, Metairie, LA 70005", 18],
        [`${company.name} LLC`, "45 Front St, Natchez, MS 39120", 3],
      ].map(([name, address, reviews], i) => ({
        placeId: `demo_place_${String(i + 1)}`,
        name,
        address,
        reviews,
        mapsUrl: placeMapsUrl(`demo_place_${String(i + 1)}`),
      })),
    );
  if (r === "gbp/pick") {
    const placeId = str(body.placeId);
    s.gbp.profile = { ...prof, placeId, mapsUrl: placeMapsUrl(placeId), syncedAt: new Date().toISOString() };
    return ok(s.gbp);
  }
  if (r === "gbp" && method === "PUT") {
    const problems = gbpDescriptionProblems(str(body.description), prof);
    if (problems.length) return fail(400, "description_rules", problems.join(" "));
    s.gbp.profile = {
      ...prof,
      description: str(body.description),
      services: (body.services as string[] | undefined) ?? prof.services,
    };
    return ok(s.gbp);
  }
  const reply = /^reviews\/([^/]+)\/reply$/.exec(r);
  if (reply) {
    const rv = s.gbp.reviews.find((x) => x.id === reply[1]);
    if (!rv) return fail(404, "not_found", "That review was not found.");
    const problems = replyProblems(str(body.reply));
    if (body.approve === true && problems.length) return fail(400, "reply_rules", problems.join(" "));
    rv.reply = str(body.reply);
    rv.replyStatus = body.approve === true ? "approved" : "draft";
    s.gbp.waitingReplies = s.gbp.reviews.filter((x) => x.replyStatus === "none" || x.replyStatus === "draft").length;
    return ok(s.gbp);
  }
  if (r === "gbp/posts") {
    const post = { service: str(body.service), area: str(body.area), body: str(body.body) };
    const problems = gbpPostProblems(post);
    if (!body.mediaId) problems.push("Add a real photo from a job.");
    if (problems.length) return fail(400, "post_rules", problems.join(" "));
    const later = typeof body.scheduledFor === "string" && Date.parse(body.scheduledFor) > Date.now();
    const row: GbpPostRow = {
      id: id(),
      ...post,
      mediaId: str(body.mediaId),
      status: later ? "scheduled" : "held",
      scheduledFor: later ? str(body.scheduledFor) : null,
      postedAt: null,
      createdAt: new Date().toISOString(),
    };
    s.gbp.posts.unshift(row);
    return ok(row);
  }
  if (r.startsWith("gbp/posts/") && method === "DELETE") {
    s.gbp.posts = s.gbp.posts.filter((p) => !(p.id === r.slice(10) && p.status === "scheduled"));
    return ok({ ok: true });
  }

  if (r === "media" && method === "GET") return ok({ media: s.media, visitPhotos: s.photos });
  const media = r === "media/upload" ? null : /^media\/([^/]+)$/.exec(r);
  if (media && method === "DELETE") {
    s.media = s.media.filter((x) => x.id !== media[1]);
    return ok({ media: s.media });
  }
  if (media) {
    const m = s.media.find((x) => x.id === media[1]);
    if (!m) return fail(404, "not_found", "That photo was not found.");
    m.tags = (body.tags as string[] | undefined) ?? m.tags;
    m.caption = typeof body.caption === "string" ? body.caption : m.caption;
    m.blocked = m.tags.includes("toilet");
    return ok(m);
  }
  if (r === "media/upload") {
    const row: MediaRow = {
      id: id(),
      fileId: id(),
      url: "",
      caption: str(body.caption),
      tags: [],
      source: "upload",
      blocked: false,
      used: 0,
      createdAt: new Date().toISOString(),
    };
    s.media.unshift(row);
    return ok({ media: s.media });
  }
  if (r === "social" && method === "GET") return ok({ posts: s.social });
  if (r === "social/schedule-month") {
    let scheduled = 0;
    for (const p of s.social)
      if (p.status === "draft" && p.scheduledFor?.slice(0, 7) === str(body.month) && new Date(p.scheduledFor) > new Date()) {
        p.status = "scheduled";
        scheduled += 1;
      }
    return ok({ scheduled });
  }
  const one = /^social\/([^/]+)(\/retry)?$/.exec(r);
  const old = one ? s.social.find((x) => x.id === one[1]) : undefined;
  if (one && !old) return fail(404, "not_found", "That post was not found.");
  if (old && one?.[2]) {
    old.status = "scheduled";
    old.scheduledFor = new Date(Math.max(Date.now(), Date.parse(old.scheduledFor ?? "") || 0)).toISOString();
    old.results = {};
    return ok(old);
  }
  if (old && method === "DELETE") {
    s.social = s.social.filter((x) => x !== old);
    return Promise.resolve({ status: 204, body: null });
  }
  if (r === "social" || (old && method === "PUT")) {
    const ids = (body.mediaIds as string[] | undefined) ?? [];
    const platforms = (body.platforms as SocialPostRow["platforms"] | undefined) ?? [];
    const draft = body.draft === true;
    const all = socialPostProblems({ body: str(body.body), platforms, photos: s.media.filter((m) => ids.includes(m.id)) });
    const problems = draft ? all.filter((x) => x.startsWith("That photo")) : all;
    if (platforms.length === 0) problems.push("Pick where it goes: Facebook, Instagram or Google.");
    const when = typeof body.scheduledFor === "string" ? body.scheduledFor : null;
    if (!draft && !when) problems.push("Pick the day and time.");
    if (problems.length) return fail(400, "post_rules", problems.join(" "));
    const row: SocialPostRow = {
      id: old?.id ?? id(),
      body: str(body.body),
      mediaIds: ids,
      platforms,
      status: draft ? "draft" : "scheduled",
      scheduledFor: when,
      postedAt: null,
      results: {},
      createdAt: old?.createdAt ?? new Date().toISOString(),
    };
    if (old) Object.assign(old, row);
    else s.social.unshift(row);
    return ok(row);
  }

  const [home] = DEMO_LISTINGS;
  const ranks = () => ok({ grids: s.grids, home: home!, listings: DEMO_LISTINGS, suggested: mapRankKeywords("cleaning") });
  const areasOf = (b: Record<string, unknown>): RankCircle[] => (b.areas as RankCircle[] | undefined) ?? DEMO_AREAS;
  if (r === "grids" && method === "GET") return ranks();
  if (r === "grids") {
    s.grids.push(grid(str(body.keyword) || "keyword", 4, "A competitor", areasOf(body)));
    return ranks();
  }
  if (r === "grids/area") {
    const areas = areasOf(body);
    const f = areas[0]!;
    s.grids = s.grids.map((x) => ({
      ...x,
      areas,
      lat: f.lat,
      lng: f.lng,
      size: f.size,
      radius: f.radius,
      skip: f.skip,
      miles: gridMilesApart(f.radius, f.size),
    }));
    return ranks();
  }
  const g = /^grids\/([^/]+)(\/run)?$/.exec(r);
  if (g) {
    if (method === "DELETE") s.grids = s.grids.filter((x) => x.id !== g[1]);
    else s.grids = s.grids.map((x) => (x.id === g[1] ? grid(x.keyword, 3, x.competitors[0] ?? "A competitor", x.areas) : x));
    return ranks();
  }
  const web = () =>
    ok({
      connected: true,
      site: s.web.site,
      checkedAt: s.web.checkedAt,
      windows: webRankWindows(today()),
      tracked: s.web.tracked,
      top: s.web.top,
    });
  if (r === "web-rank" && method === "GET") return web();
  if (r === "web-rank") {
    const kws = (body.keywords as string[] | undefined) ?? [];
    if (kws.length > 25) return fail(400, "bad_request", "Track 25 keywords at most.");
    s.web.tracked = kws.map(
      (k, i) => s.web.tracked.find((x) => x.keyword === k) ?? webRow(k.toLowerCase(), "tracked", 8 + i, 9 + i, 4, 210),
    );
    return web();
  }
  if (r === "web-rank/refresh") {
    s.web.checkedAt = new Date().toISOString();
    return web();
  }
  if (r === "briefs" && method === "GET") return ok({ briefs: s.briefs });
  if (r === "briefs") {
    const row = {
      ...s.briefs[0]!,
      id: id(),
      url: str(body.url),
      keyword: str(body.keyword),
      score: 58,
      checkedAt: new Date().toISOString(),
    };
    s.briefs.unshift(row);
    return ok(row);
  }
  if (/^briefs\/[^/]+\/score$/.test(r)) return ok(s.briefs[0]);
  if (r === "ads") return ok(s.ads);

  const o = s.outreach;
  if (r === "outreach" && method === "GET") return ok(o);
  if (r === "outreach/sequences") {
    const problem = outreachFromProblem(str(body.fromEmail));
    if (problem) return fail(400, "outreach_domain", problem);
    o.sequences.push({
      id: id(),
      name: str(body.name),
      fromName: str(body.fromName),
      fromEmail: str(body.fromEmail),
      mailingAddress: str(body.mailingAddress),
      steps: (body.steps as { days: number; subject: string; body: string }[] | undefined) ?? [],
      dailyCap: Number(body.dailyCap) || 30,
      ramp: true,
      sending: false,
      todayCap: 10,
      sentToday: 0,
      prospects: { active: 0, replied: 0, done: 0, stopped: 0 },
    });
    return ok(o);
  }
  const sending = /^outreach\/sequences\/([^/]+)\/sending$/.exec(r);
  if (sending) {
    const seq = o.sequences.find((x) => x.id === sending[1]);
    if (seq) seq.sending = body.on === true;
    return ok(o);
  }
  if (r === "outreach/prospects") {
    const email = str(body.email).toLowerCase();
    if (o.prospects.some((x) => x.email === email)) return fail(409, "prospect_exists", "That email is already on the list.");
    const row: ProspectRow = {
      id: id(),
      business: str(body.business),
      firstName: null,
      email,
      website: typeof body.website === "string" ? body.website : null,
      category: null,
      zip: typeof body.zip === "string" ? body.zip : null,
      sequenceId: null,
      step: 0,
      nextAt: null,
      status: "new",
      aiLine: null,
      note: null,
      leadId: null,
    };
    o.prospects.unshift(row);
    return ok(row);
  }
  if (r === "outreach/enroll") {
    const ids = (body.prospectIds as string[] | undefined) ?? [];
    let started = 0;
    for (const x of o.prospects)
      if (ids.includes(x.id) && x.status === "new") {
        Object.assign(x, { status: "active", sequenceId: str(body.sequenceId), nextAt: new Date().toISOString() });
        started += 1;
      }
    return ok({ started });
  }
  if (r === "outreach/ai-lines") {
    const ids = (body.prospectIds as string[] | undefined) ?? [];
    const out = o.prospects.filter((x) => ids.includes(x.id));
    for (const x of out)
      x.aiLine ??= `Your ${x.business.split(" ")[0] ?? ""} office on Magazine Street serves families in the neighborhood.`;
    return ok({ prospects: out });
  }
  const rep = /^outreach\/prospects\/([^/]+)\/reply$/.exec(r);
  if (rep) {
    const x = o.prospects.find((y) => y.id === rep[1]);
    if (!x) return fail(404, "not_found", "That business was not found.");
    const stop = /remove me|stop|unsubscribe|not interested/i.test(str(body.body));
    Object.assign(
      x,
      stop ? { status: "unsub", note: "Asked to stop", nextAt: null } : { status: "replied", leadId: id(), nextAt: null },
    );
    return ok(x);
  }
  if (r === "finder" && method === "GET") return ok({ searches: s.finder });
  if (r === "finder") {
    const row: FinderRow = {
      id: id(),
      category: str(body.category),
      area: str(body.area),
      big: body.big === true,
      target: body.big === true ? 2000 : 60,
      pages: 2,
      pagesPaid: 2,
      found: 40,
      added: 14,
      skipped: 26,
      spare: body.big === true ? 12 : 0,
      status: body.big === true ? "running" : "done",
      note: null,
      createdAt: new Date().toISOString(),
    };
    s.finder.unshift(row);
    return ok(row);
  }
  return null;
}
